import { memo } from 'react';
import { twMerge, useWindowSize, BREAKPOINTS_WIDTHS } from '@belong/common';
import { Image } from '@belong/ui';
import { MediaModel } from 'src/api/models';

interface GalleryGridProps {
  mediaImages: MediaModel[];
  onImageClick?: (image: string) => void;
}
interface LayoutData {
  layout: string;
  numImages: number;
}

export const GalleryGrid = memo(function GalleryGrid({
  mediaImages,
  onImageClick,
}: GalleryGridProps) {
  // `!`: `url` is optional on `MediaModel`, but every use below — the layout
  // seed, the `Image` src, the click handler — already treated it as present.
  const media = mediaImages.map((image) => image.url!);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < BREAKPOINTS_WIDTHS.LG;

  const seed = hashString(media[0] || '');
  const layoutData = getRandomLayout(media, seed);

  if (!layoutData) {
    return null; // No valid layout for the remaining images
  }

  let { layout, numImages } = layoutData;

  // On smaller screens dont use 2 or 3 column layout
  if (isMobile) {
    if (numImages === 2) {
      layout = 'grid-cols-1';
      numImages = 1;
    }

    if (numImages === 3) {
      layout = 'grid-cols-2 grid-rows-2';
      numImages = 4;
    }
  }

  // Deterministic per image set: picks consistently across renders/scrolls
  const isFirstImageLarge = seededBoolean(seed + 1);
  const gridStyle =
    numImages === 3 && !isFirstImageLarge
      ? { gridTemplateColumns: '1fr 2fr' }
      : {};

  return (
    <div className="gap-sm flex flex-col">
      <div
        className={twMerge(
          'grid gap-sm',
          numImages === 3 && !isFirstImageLarge ? 'grid-flow-dense' : layout,
        )}
        style={gridStyle}
      >
        {media.slice(0, numImages).map((image, index) => {
          const imageStyle = getGridItemSpan({
            index,
            numImages,
            layout,
            isFirstImageLarge,
          });

          return (
            <div
              className={twMerge(
                imageStyle,
                !imageStyle.includes('aspect') && 'aspect-w-4 aspect-h-3',
              )}
              key={index}
            >
              <Image
                src={image}
                alt={`Image ${index + 1}`}
                className="cursor-pointer object-cover"
                style={{ maxHeight: numImages === 4 ? 320 : 640 }}
                onClick={() => onImageClick?.(image)}
              />
            </div>
          );
        })}
      </div>
      <GalleryGrid
        mediaImages={mediaImages.slice(numImages)}
        onImageClick={onImageClick}
      />
    </div>
  );
});

// Returns `undefined` for an empty list — that is the recursion's base case,
// which the caller already handles by rendering nothing. The declared return
// type said `LayoutData`, which made `layouts[length - 1]` look total even
// though `length - 1` is `-1` there.
function getRandomLayout(
  mediaImages: string[],
  seed: number,
): LayoutData | undefined {
  const layouts: LayoutData[] = [
    { layout: 'grid-cols-1', numImages: 1 },
    {
      layout: seededBoolean(seed + 2) ? 'grid-cols-3' : 'grid-cols-2',
      numImages: 2,
    },
    { layout: 'grid-cols-3 grid-rows-2', numImages: 3 },
    { layout: 'grid-cols-2 grid-rows-2', numImages: 4 },
  ];

  if (mediaImages.length < 4) {
    const layoutIndex = mediaImages.length - 1;
    return layouts[layoutIndex];
  }

  const idx = seed % 4;
  // `!`: `seed % 4` is always a valid index into the four layouts.
  return layouts[idx]!;
}

type GridItemSpanParams = {
  numImages: number;
  layout: string;
  index: number;
  isFirstImageLarge: boolean;
};

function getGridItemSpan({
  numImages,
  layout,
  index,
  isFirstImageLarge,
}: GridItemSpanParams) {
  if (numImages === 2) {
    // If the layout is 2 rows, both are the same size
    if (layout === 'grid-cols-2') return 'grid-span-1';
    // If the layout is 3 rows, one image is larger
    if (index === 0) {
      return isFirstImageLarge ? 'col-span-2' : 'col-span-1';
    }
    return isFirstImageLarge ? 'col-span-1' : 'col-span-2';
  } else if (numImages === 3) {
    if (index === 0) {
      return isFirstImageLarge
        ? 'col-span-2 row-span-2'
        : 'col-start-2 col-end-3 row-span-2';
    }
    return isFirstImageLarge ? 'col-span-1 row-span-1' : '';
  } else if (numImages === 4) {
    return 'row-span-1 aspect-w-16 aspect-h-9';
  } else {
    return 'col-span-1 aspect-w-16 aspect-h-9 mb-0 xl:-mb-sm';
  }
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h | 0);
}

function seededBoolean(seed: number): boolean {
  // Mulberry-style mixer — deterministic for a given seed
  let t = seed + 0x6d2b79f5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) % 2 === 0;
}
